import { useState, useEffect } from "react";
import API from "@/lib/api";
import { formatDistanceToNow } from "date-fns";
import { id } from "date-fns/locale";
import { useLiveUpdate } from "@/contexts/LiveUpdateContext";
import { Clock, ChevronLeft, ChevronRight, Activity, ArrowLeftRight, Edit3, CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ActivityLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const { updateTrigger } = useLiveUpdate();

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const res = await API.get(`/logs?limit=20&page=${currentPage}`);
        setLogs(res.data.data || res.data); // Fallback for old/new format
        setTotalItems(res.data.total || 0);
        setTotalPages(Math.ceil((res.data.total || res.data.length || 0) / 20));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [updateTrigger, currentPage]);

  const renderChanges = (log) => {
    if (!log.old_data || !log.new_data) return null;
    try {
      const oldData = typeof log.old_data === "string" ? JSON.parse(log.old_data) : log.old_data;
      const newData = typeof log.new_data === "string" ? JSON.parse(log.new_data) : log.new_data;

      if (oldData.status && newData.status && oldData.status !== newData.status) {
        return (
          <div className="flex items-center gap-4 bg-slate-50 dark:bg-slate-800 p-4 rounded-xl mt-4 w-full">
            <div className="flex-1">
              <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Status Sebelumnya</span>
              <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400 font-medium">
                <span className="text-sm capitalize">{oldData.status.replace("_", " ")}</span>
              </div>
            </div>
            <div className="text-slate-300 dark:text-slate-600">
              <ArrowRight className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Status Baru</span>
              <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 font-medium">
                <span className="text-sm capitalize">{newData.status.replace("_", " ")}</span>
              </div>
            </div>
          </div>
        );
      }

      if (oldData.priority && newData.priority && oldData.priority !== newData.priority) {
        return (
          <div className="flex items-center gap-4 bg-slate-50 dark:bg-slate-800 p-4 rounded-xl mt-4 w-full">
            <div className="flex-1">
              <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Awalnya</span>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold capitalize">
                {oldData.priority}
              </div>
            </div>
            <div className="text-slate-300 dark:text-slate-600">
              <ArrowRight className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Menjadi</span>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-xs font-semibold capitalize">
                {newData.priority}
              </div>
            </div>
          </div>
        );
      }

      // Default fallback for other changes
      const changes = [];
      for (const key in newData) {
        if (oldData[key] !== newData[key]) {
          changes.push(
            <div key={key} className="flex items-center gap-2 text-sm bg-slate-50 dark:bg-slate-800 p-2.5 rounded-lg mt-2">
              <span className="font-semibold text-slate-500 dark:text-slate-400 capitalize">{key.replace("_", " ")}:</span>
              <span className="line-through text-slate-400 dark:text-slate-500">{oldData[key] === null || oldData[key] === "" ? "Belum Ditugaskan" : String(oldData[key])}</span>
              <ArrowRight className="w-3 h-3 text-slate-400 dark:text-slate-500" />
              <span className="text-indigo-600 dark:text-indigo-400 font-semibold">{newData[key] === null || newData[key] === "" ? "Belum Ditugaskan" : String(newData[key])}</span>
            </div>
          );
        }
      }
      return changes.length > 0 ? <div className="mt-4">{changes}</div> : null;
    } catch (e) {
      return null;
    }
  };

  const getLogIcon = (action) => {
    if (action.includes("moved")) return <ArrowLeftRight className="w-5 h-5 text-indigo-600" />;
    if (action.includes("priority") || action.includes("deadline") || action.includes("updated")) return <Edit3 className="w-5 h-5 text-orange-600" />;
    if (action.includes("created")) return <CheckCircle className="w-5 h-5 text-emerald-600" />;
    return <Activity className="w-5 h-5 text-slate-500" />;
  };

  const getLogIconBg = (action) => {
    if (action.includes("moved")) return "bg-indigo-100";
    if (action.includes("priority") || action.includes("deadline") || action.includes("updated")) return "bg-orange-100";
    if (action.includes("created")) return "bg-emerald-100";
    return "bg-slate-100";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6">
      {/* Header Section */}
      <div className="mb-12">
        <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Outfit, Plus Jakarta Sans, sans-serif' }}>Log Aktivitas</h2>
        <p className="text-slate-500 dark:text-slate-400">Jejak audit waktu nyata dari transisi dan pembaruan ruang kerja Anda.</p>
      </div>

      {/* Timeline Container */}
      <div className="relative">
        {logs.length === 0 ? (
          <div className="text-center py-12 text-slate-500 dark:text-slate-400">
            Tidak ada log aktivitas ditemukan.
          </div>
        ) : (
          <div className="relative">
            {/* Vertical Connector Line */}
            <div className="absolute left-[19px] top-4 bottom-0 w-[2px] bg-slate-200/60 dark:bg-slate-700/60"></div>
            
            <div className="space-y-12">
              {logs.map((log) => {
                const date = new Date(log.created_at || log.timestamp);
                const userName = log.user_name || "System";
                
                return (
                  <div key={log.id} className="relative flex gap-8" data-testid={`log-item-${log.id}`}>
                    {/* Timeline Icon Anchor */}
                    <div className="relative z-10 flex-shrink-0">
                      <div className={`w-10 h-10 rounded-full ${getLogIconBg(log.action)} flex items-center justify-center shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50`}>
                        {getLogIcon(log.action)}
                      </div>
                    </div>
                    
                    {/* Activity Card */}
                    <div className="flex-1 bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] dark:shadow-[0_10px_40px_-10px_rgba(0,0,0,0.2)] border border-slate-100 dark:border-slate-800 group transition-all hover:shadow-[0_10px_40px_-10px_rgba(79,70,229,0.08)] dark:hover:shadow-[0_10px_40px_-10px_rgba(79,70,229,0.15)]">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center overflow-hidden shrink-0">
                            {log.user?.picture ? (
                              <img 
                                src={log.user.picture.startsWith('http') ? log.user.picture : `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${log.user.picture}`} 
                                alt={userName} 
                                className="w-full h-full object-cover" 
                              />
                            ) : (
                              <span className="text-[10px] font-bold text-indigo-700 dark:text-indigo-400 uppercase tracking-wider">
                                {userName.substring(0, 2)}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                            <span className="font-bold text-slate-900 dark:text-white mr-1">{userName}</span>
                            <span dangerouslySetInnerHTML={{ __html: log.action }} />
                          </p>
                        </div>
                        <span className="text-xs text-slate-400 dark:text-slate-500 font-medium shrink-0 pt-1" title={date.toLocaleString("id-ID")}>
                          {formatDistanceToNow(date, { addSuffix: true, locale: id })}
                        </span>
                      </div>
                      
                      {renderChanges(log)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-12 pt-6 border-t border-slate-100 dark:border-slate-800">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Menampilkan <span className="font-medium">{(currentPage - 1) * 20 + 1}</span> hingga <span className="font-medium">{Math.min(currentPage * 20, totalItems)}</span> dari <span className="font-medium">{totalItems}</span> log
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="h-9 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-xl px-4"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Sebel
              </Button>
              
              <div className="flex items-center gap-1 hidden sm:flex">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-9 h-9 flex items-center justify-center rounded-xl text-sm font-medium transition-colors
                        ${currentPage === pageNum 
                          ? "bg-indigo-600 text-white shadow-md shadow-indigo-600/20 dark:bg-indigo-500" 
                          : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="h-9 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-xl px-4"
              >
                Lanjut <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
